import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { UserCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ProfileContactFields } from './ProfileContactFields';
import { onboardingError } from './api';
import { Top10Location } from '../top10/Top10Location';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const ProfileSetupForm = ({ user, profile, onComplete }) => {
  const [email, setEmail] = useState(profile?.email || user?.email || '');
  const [username, setUsername] = useState(profile?.username || '');
  const [contact, setContact] = useState({ city: '', state: '', country: '', phone_number: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [top10Location, setTop10Location] = useState(null);
  const lock = useRef(false);
  useEffect(() => {
    if (profile?.contact) setContact(profile.contact);
    else if (profile?.phone_number) setContact(old => ({ ...old, phone_number: profile.phone_number }));
  }, [profile]);
  const submit = async event => {
    event.preventDefault();
    if (lock.current) return;
    if (!/^[a-z0-9_]{3,20}$/.test(username.trim())) { setError('Username must be 3–20 letters, numbers, or underscores.'); return; }
    if (!contact.city.trim() || !contact.state.trim() || !contact.country || !contact.phone_number.trim()) {
      setError('City, state/region, country, and phone number are required.'); return;
    }
    lock.current = true; setLoading(true); setError('');
    try {
      await axios.put(`${API}/profile/me`, { email: email.trim().toLowerCase(), username: username.trim(), ...contact });
      await onComplete();
    } catch (err) { setError(onboardingError(err, 'Could not save your profile. Please retry.')); }
    finally { lock.current = false; setLoading(false); }
  };
  return <div className="space-y-5" data-testid="profile-setup-panel">
    <header className="text-center space-y-2"><div className="mx-auto mb-3 w-14 h-14 bg-green-100 rounded-full grid place-items-center"><UserCircle size={32} className="text-green-700" /></div>
      <p className="text-xs text-[var(--ps-text-muted)]" data-testid="profile-setup-progress">Step 1 of 2</p>
      <h2 className="text-lg font-bold" data-testid="profile-setup-title">Create Your Profile</h2>
      <p className="text-sm text-[var(--ps-text-secondary)] break-words" data-testid="profile-setup-welcome">Welcome, {user?.name}!</p>
    </header>
    <form onSubmit={submit} className="space-y-4" data-testid="profile-setup-form">
      {error && <p role="alert" className="text-sm text-red-600" data-testid="profile-setup-error">{error}</p>}
      <div><Label htmlFor="profile-email">Email address</Label><Input className="dark-input" id="profile-email" data-testid="profile-email" type="email" autoComplete="email" value={email} required maxLength={254} disabled={loading} onChange={e => setEmail(e.target.value)} /></div>
      <div><Label htmlFor="profile-username">Username</Label><Input className="dark-input" id="profile-username" data-testid="profile-username" autoComplete="username" value={username} required minLength={3} maxLength={20} disabled={loading} onChange={e => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))} placeholder="golfer123" /></div>
      <ProfileContactFields value={contact} onChange={setContact} disabled={loading} />
      <Top10Location savedAt={top10Location?.location_saved_at} disabled={loading} onSaved={setTop10Location} />
      <Button type="submit" className="ps-on-color w-full bg-green-700 hover:bg-green-800 text-white" disabled={loading} data-testid="profile-setup-continue">{loading && <Loader2 className="mr-2 animate-spin" size={18} />}{loading ? 'Saving…' : 'Continue'}</Button>
    </form>
  </div>;
};